import type { Capability } from '@gsls/shared';

/**
 * Which dashboard a person sees.
 *
 * The choice is *presentation*, exactly like a role's `sections`: it decides
 * which work a workspace puts first. It is deliberately not a permission — every
 * action inside a preset is still gated on a capability, and the API enforces
 * the same rule. The built-in roles map to a tailored preset by code; a custom
 * role instead gets a composed dashboard built from the capabilities it holds,
 * so an administrator's role is neither over- nor under-served.
 */
export type DashboardPreset =
  | 'warehouse'
  | 'production'
  | 'quality'
  | 'viewer'
  | 'admin'
  | 'custom';

const SYSTEM_PRESETS: Record<string, DashboardPreset> = {
  admin: 'admin',
  warehouse: 'warehouse',
  production: 'production',
  qc: 'quality',
  viewer: 'viewer',
};

/** The capability groups that pull a domain's widgets onto a composed dashboard. */
const DOMAIN_CAPABILITIES: { domain: DashboardPreset; capabilities: Capability[] }[] = [
  { domain: 'warehouse', capabilities: ['inventory.manage'] },
  { domain: 'production', capabilities: ['batch.manage', 'batch.override', 'recovery.record'] },
  { domain: 'quality', capabilities: ['qc.release', 'recovery.overridePolicy'] },
  { domain: 'admin', capabilities: ['users.manage', 'audit.read'] },
];

export function resolveDashboardPreset(
  roleCode: string | null,
  capabilities: readonly Capability[],
): DashboardPreset {
  if (roleCode && SYSTEM_PRESETS[roleCode]) return SYSTEM_PRESETS[roleCode];
  return capabilities.length > 0 ? 'custom' : 'viewer';
}

/** The domains a composed (custom-role) dashboard should present, in a stable order. */
export function dashboardDomains(capabilities: readonly Capability[]): DashboardPreset[] {
  const held = new Set(capabilities);
  return DOMAIN_CAPABILITIES.filter((entry) =>
    entry.capabilities.some((capability) => held.has(capability)),
  ).map((entry) => entry.domain);
}
